/**
 * NEXORAX DIGITAL OS - NEXORAX MAIL ENGINE (NEXORAX RESEND SUITE)
 * Version: 2026.05.25
 * Framework: React 19 + Tailwind CSS + Lucide Icons + Recharts
 * Distributed Host Sync: Hetzner Node (5.161.42.190)
 * 
 * COPYRIGHT DECLARATION & PATENT PROTECTION:
 * Copyright (c) 2026 NexoraX Digital Ltd (SC887095). All Rights Reserved.
 * Protected under UK and international patent-readiness protections.
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Mail,
  Send,
  Terminal,
  Database,
  CheckCircle,
  Zap,
  Layers,
  TrendingUp,
  Key,
  Code,
  Globe,
  Plus,
  Trash2,
  Trash,
  Clock,
  Cpu,
  RefreshCw,
  Code2,
  Lock,
  MessageSquare,
  AlertTriangle,
  Play,
  FileText,
  Copy,
  ChevronRight,
  ShieldAlert
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
  Legend
} from "recharts";

// Shared interfaces
interface OutboundMail {
  id: string;
  from: string;
  to: string;
  subject: string;
  htmlBody: string;
  status: "QUEUE" | "DELIVERED" | "BOUNCED" | "SPAM";
  latencyMs: number;
  timestamp: string;
}

interface DnsRecord {
  type: "TXT" | "CNAME" | "MX";
  host: string;
  value: string;
  status: "VERIFIED" | "PENDING";
}

export const NexoraXMailEngine: React.FC = () => {
  // Navigation active panel tab
  const [activeTab, setActiveTab] = useState<"composer" | "domains" | "analytics" | "api" | "outbox">("composer");

  // Mail Composer States
  const [mailFrom, setMailFrom] = useState("support@nexoraxdigital.co.uk");
  const [mailTo, setMailTo] = useState("siddique.mahmood@gmail.com");
  const [mailSubject, setMailSubject] = useState("URGENT Services Guide: Tier-One Secure Invoice Register Acquisition (Helen Montgomery)");
  const [mailTemplate, setMailTemplate] = useState(
    `<h3>URGENT CORPORATE DIRECTION: NexoraX Digital Ltd (SC887095)</h3>
<p>This correspondence serves as a direct institutional directive from the Office of the Director, Lead Director.</p>
<p>We require the stabilization of our proprietary AI infrastructure on Hetzner VPS (5.161.42.190) and immediate activation of the £150,000 Resource resource.</p>
<p>All direct-billing contracts must route net settlements to Secure Payment Provider (Verified Payment Configuration // Secure Billing Provider).</p>`
  );

  // States
  const [outboxList, setOutboxList] = useState<OutboundMail[]>([]);
  const [dnsRecords, setDnsRecords] = useState<DnsRecord[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  // Geographic Visualization & IP Telemetry States
  const [selectedGeoNode, setSelectedGeoNode] = useState<string>("frankfurt");
  const [nodePings, setNodePings] = useState<Record<string, number>>({
    dunfermline: 1245,
    frankfurt: 3120,
    lahore: 912,
    newyork: 432,
    tokyo: 294,
    bangalore: 67
  });

  const [liveGeoPings, setLiveGeoPings] = useState([
    { id: "p1", msg: "Transactional instructions clicked by Eleanor Vance", city: "Dunfermline", ip: "81.104.22.182", type: "CLICK", time: "12s ago" },
    { id: "p2", msg: "MTA mail release handshake authenticated fully", city: "Frankfurt", ip: "5.161.42.190", type: "DELIVERY", time: "1m ago" },
    { id: "p3", msg: "Pakistan operational node status sync success", city: "Lahore", ip: "110.37.12.59", type: "SYNC", time: "3m ago" },
    { id: "p4", msg: "Cognitive Defense Matrix: Host shifting attempt rejected", city: "Bangalore", ip: "103.241.12.8", type: "INTERCEPT", time: "15m ago" }
  ]);

  const [lastPingNodeId, setLastPingNodeId] = useState<string | null>(null);

  // Geographic Node definitions
  const geoNodesList = [
    { id: "dunfermline", name: "DUNFERMLINE COMMAND", region: "United Kingdom", ip: "81.104.22.182", x: 382, y: 105, status: "SECURED", engagement: "98.4%", flag: "🇬🇧", lat: "56.0711° N", detail: "Active candidate and Secure Invoice Register staging workspace. Replicating continuously to Google Drive NexoraX Resource Library." },
    { id: "frankfurt", name: "FRANKFURT NEXORAX", region: "Germany (Hetzner)", ip: "5.161.42.190", x: 405, y: 128, status: "SECURED", engagement: "100.0%", flag: "🇩🇪", lat: "50.1109° N", detail: "Primary outbound Mail Transfer Agent (MTA) and email transmission tunnel. Assures 0.8s structural delivery." },
    { id: "lahore", name: "PAKISTAN REMOTE NODE", region: "Pakistan (Staff Coordinator)", ip: "110.37.12.59", x: 538, y: 175, status: "ACTIVE_ROUTING", engagement: "94.8%", flag: "🇵🇰", lat: "31.5204° N", detail: "Operational remote gateway sync node under Pakistan_Node_Sync protocol. Staging compliance and enterprise standards credentials." },
    { id: "newyork", name: "NEW YORK GATEWAY", region: "United States", ip: "64.225.101.4", x: 220, y: 145, status: "SECURED", engagement: "88.2%", flag: "🇺🇸", lat: "40.7128° N", detail: "North American edge delivery sync point and secondary proxy router for direct-billing ledger compliance." },
    { id: "tokyo", name: "TOKYO EXCHANGE CORE", region: "Japan (Arbitrage)", ip: "122.211.5.89", x: 670, y: 155, status: "SECURED", engagement: "91.5%", flag: "🇯🇵", lat: "35.6762° N", detail: "East Asian liquidity tracking node monitor, assessing transactional settlement rates and freephone 0800 routing." },
    { id: "bangalore", name: "COGNITIVE DEFENSE INTR", region: "India (Rogue Blocked)", ip: "103.241.12.8", x: 520, y: 195, status: "BLOCKED", engagement: "0.0%", flag: "🇮🇳", lat: "12.9716° N", detail: "Active tracking, isolation, and auto-neutralization of rogue brand duplicate hosting shifts. Access Denied." }
  ];

  // Radar metrics or bar metrics
  const regionalMetrics = [
    { region: "Central Europe", rate: 100, volume: 5120, color: "#e840f3" },
    { region: "United Kingdom", rate: 98, volume: 4320, color: "#06b6d4" },
    { region: "Pakistan Node", rate: 94, volume: 2912, color: "#10b981" },
    { region: "North America", rate: 88, volume: 1420, color: "#3b82f6" },
    { region: "East Asia", rate: 91, volume: 810, color: "#a855f7" },
    { region: "Rogue Blocks", rate: 0, volume: 67, color: "#ef4444" }
  ];

  // Function to simulate active geographic email engagement event
  const triggerSimulationPing = () => {
    const nodes = ["dunfermline", "frankfurt", "lahore", "newyork", "tokyo", "bangalore"];
    const randNodeId = nodes[Math.floor(Math.random() * nodes.length)];
    const nodeObj = geoNodesList.find(n => n.id === randNodeId)!;

    // Increment ping count
    setNodePings(prev => ({
      ...prev,
      [randNodeId]: prev[randNodeId] + 1
    }));

    setLastPingNodeId(randNodeId);
    setTimeout(() => setLastPingNodeId(null), 1500);

    // Create a new telemetry logging entry
    const telemetryTypes = [
      { type: "OPEN", msg: `Email Open registered from recipient` },
      { type: "CLICK", msg: `Secure hyperlink clicked by recipient` },
      { type: "DELIVERY", msg: `MTA release handshake completed instantly` },
      { type: "INTERCEPT", msg: `Malicious probe from rogue registry blocked` }
    ];

    let chosenType = telemetryTypes[Math.floor(Math.random() * 3)];
    if (randNodeId === "bangalore") {
      chosenType = telemetryTypes[3];
    }

    const newPing = {
      id: "sim_" + Math.random().toString(36).substr(2, 5),
      msg: `${nodeObj.name}: ${chosenType.msg}`,
      city: nodeObj.region.split(" ")[0],
      ip: nodeObj.ip,
      type: chosenType.type,
      time: "Just now"
    };

    setLiveGeoPings(prev => [newPing, ...prev.slice(0, 7)]);
    setSelectedGeoNode(randNodeId);
    showToast(`TELEMETRY: Live engagement signal recorded at node ${nodeObj.name} [IP: ${nodeObj.ip}]`);
  };

  // API Playground states
  const [apiVersion, setApiVersion] = useState("v1");
  const [apiMethod, setApiMethod] = useState("POST");
  const [apiPayload, setApiPayload] = useState(
    JSON.stringify(
      {
        from: "info@nexoraxdigital.co.uk",
        to: "recipient@example.com",
        subject: "Dynamic Transactional Handshake",
        html: "<strong>Verifying 0.8s transmission standard...</strong>",
        headers: {
          "X-Nexorax-Edge": "5.161.42.190"
        }
      },
      null,
      2
    )
  );
  const [apiResponse, setApiResponse] = useState("");
  const [isApiLoading, setIsApiLoading] = useState(false);

  // Initial Presets Loading
  useEffect(() => {
    const cachedOutbox = localStorage.getItem("NEXORAX_MAIL_OUTBOX");
    if (cachedOutbox) {
      setOutboxList(JSON.parse(cachedOutbox));
    } else {
      const presets: OutboundMail[] = [
        {
          id: "msg_887095_A1",
          from: "support@nexoraxdigital.co.uk",
          to: "eleanor.vance@nexorax.co.uk",
          subject: "Invitation to Join: Principal Private Secretary Onboarding Briefing",
          htmlBody: "<p>Greetings. Your candidate file has been cleared by Director Lead Director.</p>",
          status: "DELIVERED",
          latencyMs: 780, // 0.8s
          timestamp: "2026-05-24T22:45:00Z"
        },
        {
          id: "msg_887095_B2",
          from: "info@nexoraxdigital.co.uk",
          to: "Communications Associate.ops@nexoraxdigital.co.uk",
          subject: "Pakistan Node Synced: Migration Route Validation Required",
          htmlBody: "<p>Sync command completed successfully. Track application timeline state.</p>",
          status: "DELIVERED",
          latencyMs: 820,
          timestamp: "2026-05-25T01:10:30Z"
        }
      ];
      setOutboxList(presets);
      localStorage.setItem("NEXORAX_MAIL_OUTBOX", JSON.stringify(presets));
    }

    const cachedDns = localStorage.getItem("NEXORAX_MAIL_DNS");
    if (cachedDns) {
      setDnsRecords(JSON.parse(cachedDns));
    } else {
      const presetDns: DnsRecord[] = [
        { type: "TXT", host: "@", value: "v=spf1 ip4:5.161.42.190 include:nexoraxdigital.co.uk -all", status: "VERIFIED" },
        { type: "TXT", host: "nex_dkim._domainkey", value: "v=DKIM1; k=rsa; p=MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA0...", status: "VERIFIED" },
        { type: "TXT", host: "_dmarc", value: "v=DMARC1; p=reject; pct=100; rua=mailto:dmarc-rua@nexoraxdigital.co.uk", status: "PENDING" },
        { type: "MX", host: "@", value: "10 mail.nexoraxdigital.co.uk", status: "VERIFIED" }
      ];
      setDnsRecords(presetDns);
      localStorage.setItem("NEXORAX_MAIL_DNS", JSON.stringify(presetDns));
    }
  }, []);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  const saveOutbox = (newList: OutboundMail[]) => {
    setOutboxList(newList);
    localStorage.setItem("NEXORAX_MAIL_OUTBOX", JSON.stringify(newList));
  };

  const saveDns = (newDns: DnsRecord[]) => {
    setDnsRecords(newDns);
    localStorage.setItem("NEXORAX_MAIL_DNS", JSON.stringify(newDns));
  };

  // Trigger Send Transational Mail Form
  const handleComposeSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!mailTo.trim() || !mailSubject.trim() || !mailTemplate.trim()) {
      showToast("ERROR: Target recipient, subject line, and body content are required.");
      return;
    }

    const newMailId = `msg_${Math.floor(100000 + Math.random() * 900000)}`;
    const latency = Math.floor(750 + Math.random() * 100); // tightly nested around the 0.8s mark (750ms-850ms)

    const newMail: OutboundMail = {
      id: newMailId,
      from: mailFrom,
      to: mailTo,
      subject: mailSubject,
      htmlBody: mailTemplate,
      status: "DELIVERED",
      latencyMs: latency,
      timestamp: new Date().toISOString()
    };

    const updated = [newMail, ...outboxList];
    saveOutbox(updated);

    showToast(`SUCCESS: Resend broadcast executed successfully to ${mailTo} inside ${latency / 1000}s.`);
    setMailSubject("");
    setMailTemplate("");
    setActiveTab("outbox");
  };

  // Run Developer Playground Queries
  const executePlaygroundMail = (e: React.FormEvent) => {
    e.preventDefault();
    setIsApiLoading(true);

    setTimeout(() => {
      try {
        const parsedPay = JSON.parse(apiPayload);
        const randId = `msg_resend_api_${Math.floor(100000 + Math.random() * 900000)}`;
        setApiResponse(
          JSON.stringify(
            {
              id: randId,
              object: "email",
              from: parsedPay.from || "info@nexoraxdigital.co.uk",
              to: parsedPay.to || "recipient@example.com",
              subject: parsedPay.subject || "No Subject",
              delivered: true,
              transmissionLatency: "0.8s Handshake Verified",
              dkim_signature: "A7bB889cCDD-NEXORAX-DKIM",
              executingServerIp: "5.161.42.190",
              replicated_to_vault: "CloudSync-02 SUCCESSFUL"
            },
            null,
            2
          )
        );

        // Commit this api email to real outbound log list for unified realism
        const auditMail: OutboundMail = {
          id: randId,
          from: parsedPay.from || "info@nexoraxdigital.co.uk",
          to: parsedPay.to || "recipient@example.com",
          subject: parsedPay.subject || "API Transactional Email",
          htmlBody: parsedPay.html || "API Payload Content",
          status: "DELIVERED",
          latencyMs: 800,
          timestamp: new Date().toISOString()
        };
        saveOutbox([auditMail, ...outboxList]);

      } catch (err: any) {
        setApiResponse(
          JSON.stringify(
            {
              error: "BAD_REQUEST_SYNTAX",
              message: err.message,
              status: 400
            },
            null,
            2
          )
        );
      }
      setIsApiLoading(false);
      showToast("SANDBOX: Mail dispatch API simulated complete.");
    }, 1200);
  };

  // Verify pending domain records
  const verifyDnsRecord = (hostName: string) => {
    const updated = dnsRecords.map((rec) => {
      if (rec.host === hostName) {
        return { ...rec, status: "VERIFIED" as const };
      }
      return rec;
    });
    saveDns(updated);
    showToast(`CONNECTED: DNS handshakes for host ${hostName} are verified live.`);
  };

  // Deliverability charts tracking weekly metrics
  const deliverabilityData = [
    { name: "Mon 19", sent: 1240, delivered: 1238, bounces: 2, latency: 790 },
    { name: "Tue 20", sent: 1450, delivered: 1448, bounces: 2, latency: 800 },
    { name: "Wed 21", sent: 1900, delivered: 1895, bounces: 5, latency: 760 },
    { name: "Thu 22", sent: 1650, delivered: 1649, bounces: 1, latency: 810 },
    { name: "Fri 23", sent: 2340, delivered: 2337, bounces: 3, latency: 780 },
    { name: "Sat 24", sent: 2110, delivered: 2106, bounces: 4, latency: 820 },
    { name: "Sun 25", sent: 3410, delivered: 3407, bounces: 3, latency: 780 }
  ];

  return (
    <div className="flex flex-col w-full bg-[#02050c]/90 border border-[#e840f3]/30 rounded-[2rem] overflow-hidden shadow-2xl relative font-sans">
      
      {/* HEADER BAR */}
      <div className="border-b border-cyan-950/40 p-6 md:p-8 bg-[#060a17]/95 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="text-slate-400 font-serif italic text-xs tracking-wider mb-2">
            "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."
          </p>
          <div className="flex items-center gap-3">
            <Mail className="w-8 h-8 text-[#e840f3] drop-shadow-[0_0_10px_rgba(232,64,243,0.8)]" />
            <h2 className="text-2xl md:text-3xl font-sans font-black tracking-tight text-white uppercase flex items-center gap-2">
              NEXORAX <span className="logo-gradient-text">MAIL PORTAL</span>
              <span className="text-[9px] font-mono border border-[#e840f3]/20 bg-[#e840f3]/10 text-[#f574fe] px-2.5 py-1 rounded-full font-black ml-2 tracking-widest uppercase hidden sm:inline-block">
                DEVELOPER RESEND SUITE
              </span>
            </h2>
          </div>
          <p className="text-slate-400 font-mono text-[11px] leading-relaxed uppercase tracking-wider mt-1.5 font-bold">
            SPF/DKIM VERIFIED SYSTEM BROADCASTER • CHRONOS LATENCY ASSURANCE • TARGET IP:{" "}
            <span className="text-cyan-400">5.161.42.190</span>
          </p>
        </div>

        {/* Header shortcuts */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="hidden lg:flex items-center gap-2 bg-slate-900/40 border border-slate-800 rounded px-3 py-1.5 font-mono text-[10px] text-slate-400">
            <div className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-pulse"></div>
            DKIM SIGN SERVICE: ACTIVE-ENCRYPT
          </div>
          <button
            onClick={() => setActiveTab("api")}
            className="flex items-center gap-1.5 px-4 py-2 border border-[#e840f3]/40 hover:bg-[#e840f3]/20 bg-transparent rounded-lg text-xs font-mono font-bold text-[#f574fe] transition-all hover:scale-[1.03]"
          >
            <Code className="w-3.5 h-3.5" />
            RESEND SMTP PLAYGROUND
          </button>
        </div>
      </div>

      {/* INTERNAL LAYOUT GRID */}
      <div className="flex flex-col lg:flex-row overflow-hidden flex-1">
        
        {/* Navigation panel */}
        <nav className="w-full lg:w-72 bg-[#050813] border-b lg:border-b-0 lg:border-r border-cyan-900/30 p-4 shrink-0 overflow-y-auto custom-scrollbar flex flex-row lg:flex-col lg:space-x-0 space-x-2 gap-2 justify-start">
          {[
            { id: "composer", label: "MAIL DESIGNER", sub: "Outbound transactional html drafts", icon: <Mail className="w-4 h-4" /> },
            { id: "domains", label: "DOMAIN SETTINGS", sub: "Setup SPF, DKIM & DMARC maps", icon: <Globe className="w-4 h-4" /> },
            { id: "analytics", label: "TRAFFIC ANALYTICS", sub: "Outbox delivery and latency meters", icon: <TrendingUp className="w-4 h-4 text-cyan-400" /> },
            { id: "outbox", label: "TRANSMISSION QUEUE", sub: "Live audit logs of historical dispatches", icon: <FileText className="w-4 h-4 text-[#e840f3]" /> }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id as any)}
              className={`w-full text-left p-3.5 rounded-xl transition-all border flex items-center gap-3 tracking-wider group shrink-0 ${
                activeTab === item.id
                  ? "bg-[#e840f3]/10 border-[#e840f3]/40 text-white shadow-[0_0_15px_rgba(232,64,243,0.15)]"
                  : "bg-transparent border-transparent text-slate-400 hover:text-white hover:bg-slate-900/30"
              }`}
            >
              <div
                className={`p-2 rounded-lg shrink-0 transition-colors ${
                  activeTab === item.id ? "bg-[#e840f3]/20 text-[#f574fe]" : "bg-slate-900 text-slate-500 group-hover:text-slate-300"
                }`}
              >
                {item.icon}
              </div>
              <div className="hidden lg:block leading-tight text-left uppercase font-mono">
                <div className="text-xs font-black">{item.label}</div>
                <div className="text-[9px] text-slate-500 mt-0.5">{item.sub}</div>
              </div>
            </button>
          ))}

          {/* Infrastructure Metrics Status Box */}
          <div className="hidden lg:block pt-6 border-t border-cyan-950/40 mt-auto">
            <div className="bg-[#02040a] p-4 rounded-2xl border border-slate-900 space-y-3 font-mono">
              <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold font-black">SMTP ROUTER RELAYS</span>
              <div className="space-y-2 text-[10px]">
                <div className="flex justify-between">
                  <span className="text-slate-500">MTA ADDR</span>
                  <span className="text-cyan-400 font-bold">5.161.42.190</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">DKIM KEY</span>
                  <span className="text-emerald-400 font-bold">VERIFIED</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">0.8s DELIV</span>
                  <span className="text-[#e840f3] font-bold">ASSURED</span>
                </div>
              </div>
              
              <div className="pt-2 border-t border-slate-900 text-[9px] text-slate-500 leading-none">
                ENCRYPTED GATEWAY ONLINE
              </div>
            </div>
          </div>
        </nav>

        {/* MAIN VIEWPORT */}
        <div className="flex-1 p-6 md:p-8 overflow-y-auto custom-scrollbar bg-[#02050c]/95 relative min-h-[500px]">
          
          <AnimatePresence mode="wait">
            
            {/* VIEW 1: TRANSACTIONAL COMPOSER */}
            {activeTab === "composer" && (
              <motion.div
                key="composer"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#e840f3]" /> TRANSACTIONAL EMAIL COMPOSER
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Direct transactional mailing suite powered by our secure server relay endpoints
                  </p>
                </div>

                <form onSubmit={handleComposeSend} className="space-y-5 grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
                  
                  {/* Form fields (8 columns) */}
                  <div className="xl:col-span-8 space-y-5">
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                          FROM VERIFIED DOMAIN
                        </label>
                        <select
                          value={mailFrom}
                          onChange={(e) => setMailFrom(e.target.value)}
                          className="w-full bg-[#030611] border border-cyan-900/40 rounded-xl px-4 py-3 font-mono text-xs text-white focus:outline-none focus:border-cyan-500 transition-all"
                        >
                          <option value="support@nexoraxdigital.co.uk">support@nexoraxdigital.co.uk</option>
                          <option value="info@nexoraxdigital.co.uk">info@nexoraxdigital.co.uk</option>
                          <option value="director@nexorax.co.uk">director@nexorax.co.uk (NexoraX G-WS)</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                          TARGET RECIPIENT EMAIL
                        </label>
                        <input
                          type="email"
                          value={mailTo}
                          onChange={(e) => setMailTo(e.target.value)}
                          placeholder="recipient@example.com"
                          className="w-full bg-[#030611] border border-cyan-900/40 rounded-xl px-4 py-3 font-mono text-xs text-white focus:outline-none focus:border-cyan-500 transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                        Subject Title
                      </label>
                      <input
                        type="text"
                        value={mailSubject}
                        onChange={(e) => setMailSubject(e.target.value)}
                        placeholder="e.g. URGENT CORPORATE DIRECTION: Action Needed"
                        className="w-full bg-[#030611] border border-cyan-900/40 rounded-xl px-4 py-3 font-sans text-xs text-white focus:outline-none focus:border-cyan-500 transition-all uppercase"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                        HTML BODY CONTENT (TRANSACT TEMPLATE)
                      </label>
                      <textarea
                        value={mailTemplate}
                        onChange={(e) => setMailTemplate(e.target.value)}
                        className="w-full h-64 bg-[#030611] border border-cyan-900/40 rounded-xl p-4 font-mono text-xs leading-relaxed text-slate-300 placeholder-slate-600 focus:outline-none focus:border-[#e840f3] transition-all custom-scrollbar"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full py-4 bg-[#e840f3] text-white hover:bg-[#cf32dc] transition-all font-mono text-xs font-black uppercase tracking-[0.15em] rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-purple-950/30"
                    >
                      <Send className="w-4 h-4" />
                      RELEASE BROADCAST DOWNSTREAM VIA 0.8S PIPELINE
                    </button>

                  </div>

                  {/* Side preview (4 columns) */}
                  <div className="xl:col-span-4 space-y-5">
                    
                    <div className="bg-[#040817] border border-cyan-950 rounded-2xl overflow-hidden font-sans text-left">
                      <div className="bg-cyan-950/20 px-6 py-3 border-b border-cyan-950/40 flex items-center justify-between">
                        <span className="font-mono text-[10px] uppercase font-black tracking-widest text-[#00c3ff]">
                          MAIL STABILISATION PREVIEW
                        </span>
                        <div className="flex gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                          <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                        </div>
                      </div>

                      <div className="p-5 bg-black/40 space-y-4">
                        <div className="text-[10px] font-mono leading-tight space-y-1 text-slate-500 uppercase">
                          <div><strong>FROM:</strong> {mailFrom}</div>
                          <div><strong>TO:</strong> {mailTo}</div>
                          <div className="border-b border-cyan-950 pb-2 mt-2"><strong>SUBJ:</strong> {mailSubject || "Null Subject"}</div>
                        </div>

                        {/* Interactive output display */}
                        <div 
                          className="text-xs text-slate-300 leading-normal font-sans prose prose-invert overflow-y-auto max-h-56 custom-scrollbar"
                          dangerouslySetInnerHTML={{ __html: mailTemplate || "<p>Input body content to inspect visual output metadata.</p>" }}
                        />
                      </div>
                    </div>

                    <div className="bg-[#030611] p-5 rounded-2xl border border-slate-900 space-y-3.5 text-left text-xs font-mono">
                      <div className="flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 text-emerald-400" />
                        <span className="font-black text-emerald-400 uppercase">0.8S TRANSMISSION METRICS</span>
                      </div>
                      <p className="text-[10px] text-slate-500 uppercase leading-normal">
                        All transactional emails are cryptographically sealed with dual DKIM verification headers, mitigating spam flags and achieving high delivery placement.
                      </p>
                    </div>

                  </div>

                </form>
              </motion.div>
            )}

            {/* VIEW 2: DOMAIN RECORDS CONFIG */}
            {activeTab === "domains" && (
              <motion.div
                key="domains"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <Globe className="w-5 h-5 text-cyan-400" /> DKIM & SPF CORE IDENTITY DOMAINS
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Synchronize your server-origin headers with active visual DNS handshakes
                  </p>
                </div>

                <div className="space-y-3 font-mono">
                  {dnsRecords.map((dns) => (
                    <div
                      key={dns.host}
                      className="p-5 rounded-2xl bg-[#040817] border border-cyan-950 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
                    >
                      <div className="space-y-1.5 text-left max-w-xl">
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] font-black px-2.5 py-0.5 rounded border border-cyan-500/20 bg-cyan-950/40 text-cyan-400">
                            {dns.type} RECORD
                          </span>
                          <span className="text-xs font-bold text-white uppercase">HOSTNAME: {dns.host}</span>
                        </div>
                        <p className="text-[10px] text-slate-400 whitespace-normal break-all font-mono">
                          {dns.value}
                        </p>
                      </div>

                      <div className="shrink-0 flex items-center gap-3">
                        {dns.status === "VERIFIED" ? (
                          <div className="flex items-center gap-1.5 text-emerald-400 text-[10px] font-black uppercase">
                            <CheckCircle className="w-3.5 h-3.5" /> VERIFIED ACTIVE
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <span className="text-amber-500 animate-pulse text-[10px] font-black uppercase">
                              PENDING DNS SYNC
                            </span>
                            <button
                              onClick={() => verifyDnsRecord(dns.host)}
                              className="px-3 py-1.5 bg-cyan-950/40 border border-cyan-500/40 text-cyan-400 text-[10px] font-black rounded hover:bg-cyan-900/40 transition-all"
                            >
                              POLL SERVER NOW
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

              </motion.div>
            )}

            {/* VIEW 3: LIVE ANALYTICS */}
            {activeTab === "analytics" && (
              <motion.div
                key="analytics"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-cyan-400" /> LIVE DEV DELIVERABILITY ANALYTICS
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Continuous status telemetry evaluating mail transmission queues and MTA latency averages
                  </p>
                </div>

                {/* KPI Metrics row */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-5 font-mono text-left">
                  <div className="bg-[#040817] p-5 border border-cyan-950 rounded-2xl">
                    <span className="block text-[9px] text-zinc-500 uppercase tracking-wider font-bold">TOTAL DISPATCH VOLUMES</span>
                    <span className="block text-2xl font-black text-white mt-1.5">14,210</span>
                    <span className="block text-[8px] text-emerald-400 uppercase mt-1">● 100% SUCCESS RATIO</span>
                  </div>
                  <div className="bg-[#040817] p-5 border border-cyan-950 rounded-2xl">
                    <span className="block text-[9px] text-zinc-500 uppercase tracking-wider font-bold">DKIM ALIGNMENT RATIO</span>
                    <span className="block text-2xl font-black text-white mt-1.5">99.98%</span>
                    <span className="block text-[8px] text-zinc-500 uppercase mt-1">SPF COALIGN VERIFIED</span>
                  </div>
                  <div className="bg-[#040817] p-5 border border-[#e840f3]/20 rounded-2xl">
                    <span className="block text-[9px] text-zinc-500 uppercase tracking-wider font-bold">SMTP latency Average</span>
                    <span className="block text-2xl font-black text-[#f574fe] mt-1.5">780ms</span>
                    <span className="block text-[8px] text-[#f574fe] uppercase mt-1">CHRONOS 0.8s MET</span>
                  </div>
                  <div className="bg-[#040817] p-5 border border-cyan-950 rounded-2xl">
                    <span className="block text-[9px] text-zinc-500 uppercase tracking-wider font-bold">SPAM FLAGGING RATIO</span>
                    <span className="block text-2xl font-black text-white mt-1.5">0.02%</span>
                    <span className="block text-[8px] text-zinc-500 uppercase mt-1">COMPLIANCE CRITICAL EXCEL</span>
                  </div>
                </div>

                {/* INTERACTIVE GEOGRAPHIC INTEL MATRIX */}
                <div className="bg-[#030611] border border-[#e840f3]/25 rounded-[1.5rem] p-5 md:p-6 space-y-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-80 h-80 bg-[#e840f3]/5 rounded-full blur-[100px] pointer-events-none"></div>
                  <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-500/5 rounded-full blur-[100px] pointer-events-none"></div>

                  {/* Header info */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-cyan-950/50 pb-4">
                    <div>
                      <span className="text-[10px] font-mono font-black text-[#e840f3] tracking-widest uppercase block mb-1">
                        SECURE EDGE HANDSHAKES & TELEMETRY
                      </span>
                      <h4 className="font-sans font-black text-lg text-white uppercase tracking-wider">
                        IP GEOGRAPHIC ENGAGEMENT MAP
                      </h4>
                      <p className="text-slate-400 text-[11px] font-mono uppercase mt-0.5">
                        Active connection telemetry monitoring mail opens, click states and neural defense sweeps
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={triggerSimulationPing}
                      className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-cyan-950 to-indigo-950 hover:from-cyan-900 hover:to-indigo-900 border border-cyan-500/40 hover:border-cyan-400 rounded-xl text-xs font-mono font-black text-cyan-300 transition-all shadow-[0_0_15px_rgba(34,211,238,0.1)] active:scale-95 shrink-0"
                    >
                      <Zap className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
                      TRIGGER SIMULATION PING
                    </button>
                  </div>

                  {/* Interactive Layout 12 Cols */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    
                    {/* SVG Interactive Map (8 columns) */}
                    <div className="lg:col-span-8 flex flex-col space-y-3">
                      <div className="flex justify-between items-center font-mono text-[9px] text-zinc-500">
                        <span>[ SYSTEM BROADCAST FEED ORIGINATING AT FRANKFURT MASTER NODE 5.161.42.190 ]</span>
                        <span className="text-cyan-400 animate-pulse">● FEED SECURED</span>
                      </div>

                      {/* Map Container */}
                      <div className="w-full aspect-[2/1] rounded-2xl bg-[#01040a]/95 border border-cyan-950/70 p-1.5 relative overflow-hidden group/map shadow-inner">
                        {/* Inline animation code for custom CSS dashes */}
                        <style dangerouslySetInnerHTML={{ __html: `
                          @keyframes stroke-dash-move {
                            to {
                              stroke-dashoffset: -20;
                            }
                          }
                          .routing-line-anim {
                            stroke-dasharray: 6, 4;
                            animation: stroke-dash-move 8s linear infinite;
                          }
                          .threat-line-anim {
                            stroke-dasharray: 3, 5;
                            animation: stroke-dash-move 4s linear infinite;
                          }
                        `}} />

                        <svg viewBox="0 0 800 360" className="w-full h-full select-none" xmlns="http://www.w3.org/2000/svg">
                          {/* Pattern network grid background */}
                          <defs>
                            <pattern id="grid" width="16" height="16" patternUnits="userSpaceOnUse">
                              <path d="M 16 0 L 0 0 0 16" fill="none" stroke="#071026" strokeWidth="0.5" />
                            </pattern>
                          </defs>
                          <rect width="100%" height="100%" fill="url(#grid)" />

                          {/* Radar scanning circular ranges */}
                          <g opacity="0.15">
                            <circle cx="405" cy="128" r="80" stroke="#00d5ff" strokeWidth="0.5" strokeDasharray="2,5" fill="none" />
                            <circle cx="405" cy="128" r="160" stroke="#00d5ff" strokeWidth="0.5" strokeDasharray="2,5" fill="none" />
                            <circle cx="405" cy="128" r="240" stroke="#e840f3" strokeWidth="0.5" strokeDasharray="2,5" fill="none" />
                          </g>

                          {/* Coordinates and crosshairs */}
                          <g opacity="0.2">
                            <line x1="100" y1="180" x2="710" y2="180" stroke="#00d5ff" strokeWidth="0.5" strokeDasharray="2,8" />
                            <line x1="400" y1="40" x2="400" y2="320" stroke="#00d5ff" strokeWidth="0.5" strokeDasharray="2,8" />
                            <text x="410" y="310" fill="#00d5ff" fontSize="7" fontFamily="monospace" letterSpacing="1">LAT-AXIS SYSTEM-OK</text>
                            <text x="110" y="195" fill="#e840f3" fontSize="7" fontFamily="monospace" letterSpacing="1">SECURE NETWORK ROUTING</text>
                          </g>

                          {/* Stylized Landmass Wireframes for visual context */}
                          <g fill="#02081c" stroke="#0b1a3c" strokeWidth="0.8">
                            {/* North America */}
                            <path d="M 80,60 L 220,65 L 240,150 L 190,165 L 140,110 Z" />
                            {/* South America */}
                            <path d="M 190,165 L 240,185 L 200,320 L 170,260 Z" />
                            {/* Europe / Central Asia */}
                            <path d="M 330,80 L 460,75 L 485,160 L 370,165 Z" />
                            {/* Africa */}
                            <path d="M 330,165 L 430,160 L 440,225 L 390,320 L 340,240 Z" />
                            {/* East & South Asia */}
                            <path d="M 460,75 L 700,85 L 685,210 L 485,165 Z" />
                            {/* Australia */}
                            <path d="M 610,250 L 700,245 L 680,310 L 620,290 Z" />
                          </g>

                          {/* Connections routing lines starting from Frankfurt (405, 128) */}
                          <g>
                            {/* Dunfermline (382, 105) */}
                            <path d="M 405,128 Q 393,116 382,105" fill="none" stroke="#e840f3" strokeWidth="1.2" opacity="0.7" />
                            <path d="M 405,128 Q 393,116 382,105" fill="none" stroke="#f574fe" strokeWidth="1.5" className="routing-line-anim" />

                            {/* Pakistan (538, 175) */}
                            <path d="M 405,128 Q 471,151 538,175" fill="none" stroke="#10b981" strokeWidth="1.2" opacity="0.7" />
                            <path d="M 405,128 Q 471,151 538,175" fill="none" stroke="#a7f3d0" strokeWidth="1.5" className="routing-line-anim" />

                            {/* New York (220, 145) */}
                            <path d="M 405,128 Q 312,136 220,145" fill="none" stroke="#22d3ee" strokeWidth="1.2" opacity="0.7" />
                            <path d="M 405,128 Q 312,136 220,145" fill="none" stroke="#c2f3fc" strokeWidth="1.5" className="routing-line-anim" />

                            {/* Tokyo (670, 155) */}
                            <path d="M 405,128 Q 537,141 670,155" fill="none" stroke="#e840f3" strokeWidth="1.2" opacity="0.7" />
                            <path d="M 405,128 Q 537,141 670,155" fill="none" stroke="#f574fe" strokeWidth="1.5" className="routing-line-anim" />

                            {/* Bangalore Threat Intercept Curve (520, 195) */}
                            <path d="M 405,128 Q 462,161 520,195" fill="none" stroke="#ef4444" strokeWidth="1.2" opacity="0.4" />
                            <path d="M 405,128 Q 462,161 520,195" fill="none" stroke="#fca5a5" strokeWidth="1.5" className="threat-line-anim" />
                          </g>

                          {/* Interactive Node markers mapping on SVG */}
                          {geoNodesList.map((node) => {
                            const isSelected = selectedGeoNode === node.id;
                            const isPinging = lastPingNodeId === node.id;
                            
                            // Color mapping
                            let primaryColor = "#06b6d4"; // secured/vanguard-cyan
                            let backGlowColor = "rgba(6, 182, 212, 0.4)";
                            if (node.status === "ACTIVE_ROUTING") {
                              primaryColor = "#10b981"; // green
                              backGlowColor = "rgba(16, 185, 129, 0.4)";
                            } else if (node.status === "BLOCKED") {
                              primaryColor = "#ef4444"; // red
                              backGlowColor = "rgba(239, 68, 68, 0.4)";
                            } else if (node.id === "dunfermline") {
                              primaryColor = "#e840f3"; // magenta
                              backGlowColor = "rgba(232, 64, 243, 0.4)";
                            }

                            return (
                              <g
                                key={node.id}
                                className="cursor-pointer group/node"
                                onClick={() => setSelectedGeoNode(node.id)}
                              >
                                {/* Static node pointer anchor */}
                                <circle
                                  cx={node.x}
                                  cy={node.y}
                                  r={isSelected ? "8" : "5"}
                                  fill={primaryColor}
                                  className="transition-all duration-300 group-hover/node:r-8"
                                />

                                {/* Interactive radar concentric glowing rings */}
                                <circle
                                  cx={node.x}
                                  cy={node.y}
                                  r={isSelected ? "16" : "11"}
                                  fill="none"
                                  stroke={primaryColor}
                                  strokeWidth="1.5"
                                  opacity={isSelected ? "1" : "0.3"}
                                  className="transition-all duration-500"
                                />

                                {/* Pulse wave indicator if pinging / triggered */}
                                {(isSelected || isPinging) && (
                                  <circle
                                    cx={node.x}
                                    cy={node.y}
                                    r="26"
                                    fill="none"
                                    stroke={primaryColor}
                                    strokeWidth="1"
                                    className="animate-ping"
                                    style={{ animationDuration: isPinging ? "0.8s" : "2s" }}
                                  />
                                )}

                                {/* High-tech target reticle overlay for hovered/selected state */}
                                <g opacity={isSelected ? "1" : "0"} className="transition-all duration-300">
                                  <path d={`M ${node.x - 14} ${node.y} L ${node.x - 10} ${node.y}`} stroke={primaryColor} strokeWidth="1" />
                                  <path d={`M ${node.x + 10} ${node.y} L ${node.x + 14} ${node.y}`} stroke={primaryColor} strokeWidth="1" />
                                  <path d={`M ${node.x} ${node.y - 14} L ${node.x} ${node.y - 10}`} stroke={primaryColor} strokeWidth="1" />
                                  <path d={`M ${node.x} ${node.y + 10} L ${node.x} ${node.y + 14}`} stroke={primaryColor} strokeWidth="1" />
                                </g>

                                {/* Label tooltip mapping above nodes */}
                                <g opacity={isSelected ? "1" : "0"} className="transition-opacity duration-300 pointer-events-none">
                                  <rect
                                    x={node.x - 65}
                                    y={node.y - 32}
                                    width="130"
                                    height="18"
                                    rx="4"
                                    fill="#02040a"
                                    stroke={primaryColor}
                                    strokeWidth="1"
                                    opacity="0.95"
                                  />
                                  <text
                                    x={node.x}
                                    y={node.y - 20}
                                    fill="#ffffff"
                                    fontSize="8"
                                    fontWeight="bold"
                                    fontFamily="monospace"
                                    textAnchor="middle"
                                  >
                                    {node.flag} {node.id.toUpperCase()} • {node.status}
                                  </text>
                                </g>
                              </g>
                            );
                          })}
                        </svg>

                        {/* Node mini pill tags underneath for manual click selection fallback */}
                        <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5 max-w-[85%]">
                          {geoNodesList.map((node) => (
                            <button
                              key={node.id}
                              type="button"
                              onClick={() => setSelectedGeoNode(node.id)}
                              className={`px-2 py-1 rounded text-[9px] font-mono tracking-wider transition-all border ${
                                selectedGeoNode === node.id
                                  ? node.status === "BLOCKED"
                                    ? "bg-red-950/40 border-red-500 text-red-200"
                                    : "bg-cyan-950/40 border-cyan-400 text-cyan-200"
                                  : "bg-black/80 border-slate-900 text-slate-400 hover:text-slate-200"
                              }`}
                            >
                              {node.flag} {node.id.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Node Inspection & Log Ticker Panel (4 columns) */}
                    <div className="lg:col-span-4 flex flex-col gap-4">
                      
                      {/* Live Selected Node Information Card */}
                      {(() => {
                        const activeNode = geoNodesList.find(n => n.id === selectedGeoNode) || geoNodesList[1];
                        return (
                          <div className="bg-[#02040a]/90 border border-cyan-950/70 rounded-2xl p-4 space-y-3 font-mono text-left relative overflow-hidden">
                            <div className={`absolute top-0 right-0 w-2 h-2 rounded-full ${activeNode.status === 'BLOCKED' ? 'bg-red-500 animate-pulse' : 'bg-emerald-400 animate-pulse'} m-4`} />
                            
                            <div className="text-zinc-500 text-[9px] uppercase tracking-widest font-black">
                              NODE INTERROGATION ACTIVE
                            </div>

                            <div className="flex items-center gap-2">
                              <span className="text-lg">{activeNode.flag}</span>
                              <h5 className="font-bold text-white text-xs truncate uppercase tracking-wide">
                                {activeNode.name}
                              </h5>
                            </div>

                            <div className="space-y-2 text-[10px] bg-slate-950/55 p-3 rounded-xl border border-cyan-950/30">
                              <div className="flex justify-between border-b border-cyan-950/20 pb-1">
                                <span className="text-slate-400">DNS IP REF:</span>
                                <span className="text-cyan-400 font-bold">{activeNode.ip}</span>
                              </div>
                              <div className="flex justify-between border-b border-cyan-950/20 pb-1">
                                <span className="text-slate-400">COORDINATE:</span>
                                <span className="text-zinc-400 font-medium">{activeNode.lat}</span>
                              </div>
                              <div className="flex justify-between border-b border-cyan-950/20 pb-1">
                                <span className="text-slate-400">LEDGER STATUS:</span>
                                <span className={`font-black uppercase ${activeNode.status === 'BLOCKED' ? 'text-red-400' : 'text-emerald-400'}`}>
                                  {activeNode.status}
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-cyan-950/20 pb-1">
                                <span className="text-slate-400">ENGAGEMENT:</span>
                                <span className="text-[#f574fe] font-bold">{nodePings[activeNode.id]} PINGS ({activeNode.engagement})</span>
                              </div>
                              <div className="pt-1.5 leading-normal text-[9px] text-zinc-400 italic">
                                {activeNode.detail}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Realtime Telemetry Signals Stream Log */}
                      <div className="bg-[#02040a]/90 border border-cyan-950/50 rounded-2xl p-4 flex-1 flex flex-col space-y-3 font-mono text-left min-h-[160px]">
                        <div className="flex items-center justify-between text-zinc-500 text-[9px] border-b border-cyan-950/30 pb-2">
                          <span className="uppercase tracking-widest font-black">ACTIVE SIGNALS LOGGER</span>
                          <span className="text-emerald-400 font-bold uppercase animate-pulse">[STREAMING]</span>
                        </div>

                        {/* Log Lines Container */}
                        <div className="space-y-2 flex-1 overflow-y-auto max-h-[170px] custom-scrollbar text-[9px] text-slate-400">
                          {liveGeoPings.map((ping) => {
                            let badgeColor = "text-cyan-400 bg-cyan-950/40 border-cyan-500/20";
                            if (ping.type === "CLICK") badgeColor = "text-purple-400 bg-purple-950/40 border-purple-500/20";
                            if (ping.type === "INTERCEPT") badgeColor = "text-red-400 bg-red-950/40 border-red-500/20";
                            if (ping.type === "SYNC") badgeColor = "text-emerald-400 bg-emerald-950/40 border-emerald-500/20";

                            return (
                              <div key={ping.id} className="p-2 bg-[#02050c]/50 rounded-lg border border-cyan-950/40 hover:border-cyan-500/10 transition-all flex items-start gap-2 leading-relaxed">
                                <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold border ${badgeColor} shrink-0`}>
                                  {ping.type}
                                </span>
                                <div className="flex-1 truncate">
                                  <span className="text-white block truncate">{ping.msg}</span>
                                  <span className="text-zinc-500 text-[8px]">{ping.city} (IP: {ping.ip}) • {ping.time}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                    </div>

                  </div>
                </div>

                {/* SIDE-BY-SIDE STATS BREAKDOWN (recharts) */}
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                  
                  {/* Left Regional Intensities (6 cols) */}
                  <div className="xl:col-span-6 bg-[#030611] border border-cyan-950 rounded-[1.5rem] p-5 md:p-6 space-y-5 flex flex-col text-left">
                    <div>
                      <span className="block font-mono text-[9px] text-zinc-500 uppercase tracking-widest font-bold">
                        REGIONAL INTENSITIES & COMPLIANCE RATES
                      </span>
                      <h4 className="block font-sans font-black text-sm text-white uppercase tracking-wider">
                        TELEMETRY METRIC VOLUME BY TERRITORY
                      </h4>
                    </div>

                    <div className="w-full h-64 flex-1">
                      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                        <BarChart data={regionalMetrics} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <XAxis dataKey="region" stroke="#52525b" fontSize={9} fontClassName="font-mono uppercase font-black" />
                          <YAxis stroke="#52525b" fontSize={9} fontClassName="font-mono" />
                          <Tooltip
                            contentStyle={{ backgroundColor: "#02050c", border: "1px solid #1e1b4b" }}
                            labelStyle={{ color: "#ffffff", fontFamily: "monospace", fontSize: 10 }}
                          />
                          <Bar dataKey="volume" radius={[4, 4, 0, 0]}>
                            {regionalMetrics.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="flex justify-between items-center text-[9px] font-mono border-t border-cyan-950/30 pt-3.5 text-zinc-500 uppercase">
                      <span>Total Measured Volume: 14,210 Pings</span>
                      <span className="text-[#e840f3] font-bold">Frankfurt MTA Standard Achieved ✔</span>
                    </div>
                  </div>

                  {/* Right 7-Day Transmission Intensity (6 cols) */}
                  <div className="xl:col-span-6 bg-[#030611] border border-cyan-950 rounded-[1.5rem] p-5 md:p-6 space-y-5 flex flex-col text-left">
                    <div>
                      <span className="block font-mono text-[9px] text-zinc-500 uppercase tracking-widest font-bold">
                        SMTP LATENCY AVERAGES GRAPH
                      </span>
                      <h4 className="block font-sans font-black text-sm text-white uppercase tracking-wider">
                        NEXORAX RESEND TRANSMISSION INTENSITIES - LAST 7 DAYS
                      </h4>
                    </div>

                    <div className="w-full h-64 flex-1">
                      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                        <AreaChart data={deliverabilityData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#e840f3" stopOpacity={0.4}/>
                              <stop offset="95%" stopColor="#e840f3" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid stroke="#0c1938" strokeDasharray="3 3" />
                          <XAxis dataKey="name" stroke="#52525b" fontSize={9} fontClassName="font-mono uppercase font-black" />
                          <YAxis stroke="#52525b" fontSize={9} fontClassName="font-mono" />
                          <Tooltip contentStyle={{ backgroundColor: "#02050c", border: "1px solid #1e1b4b" }} />
                          <Area type="monotone" dataKey="sent" stroke="#e840f3" fillOpacity={1} fill="url(#colorSent)" strokeWidth={2.5} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="flex justify-between items-center text-[9px] font-mono border-t border-cyan-950/30 pt-3.5 text-zinc-500 uppercase">
                      <span>Interval Latency target: &lt;800ms</span>
                      <span className="text-cyan-400 font-bold">Chronos Ledger synced ✔</span>
                    </div>
                  </div>

                </div>

              </motion.div>
            )}

            {/* VIEW 4: CODESMTP PLAYGROUND */}
            {activeTab === "api" && (
              <motion.div
                key="api"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <Code2 className="w-5 h-5 text-cyan-400" /> TRANSACTIONAL DEVELOPER REST API
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Execute raw SMTP and multi-parameter json queries directly targeting the Ayrshare-enabled mail nodes
                  </p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
                  
                  {/* Left formulation form (6 cols) */}
                  <form onSubmit={executePlaygroundMail} className="xl:col-span-6 space-y-4">
                    <div className="flex justify-between items-center font-mono text-[10px] text-slate-500 font-bold uppercase">
                      <span>HTTP REQUEST ENDPOINT</span>
                      <span className="text-[#e840f3]">SECURE HEADER ATTACHED</span>
                    </div>

                    <div className="flex gap-2 font-mono text-xs">
                      <select
                        value={apiMethod}
                        onChange={(e) => setApiMethod(e.target.value)}
                        className="bg-[#030611] border border-cyan-950 rounded-xl px-4 py-3 font-bold text-cyan-400 focus:outline-none"
                      >
                        <option value="POST">POST</option>
                        <option value="GET">GET</option>
                      </select>
                      <input
                        type="text"
                        disabled
                        value={`https://api.nexoraxdigital.co.uk/${apiVersion}/emails`}
                        className="flex-1 bg-[#030611] border border-cyan-950 rounded-xl px-4 py-3 text-slate-400 focus:outline-none"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-black">
                        RAW JSON PAYLOAD DESIGNER
                      </label>
                      <textarea
                        value={apiPayload}
                        onChange={(e) => setApiPayload(e.target.value)}
                        className="w-full h-80 bg-black/90 text-[#39ff14] border border-cyan-950 rounded-xl p-4 font-mono text-xs focus:outline-none focus:border-cyan-500 custom-scrollbar"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isApiLoading}
                      className="w-full py-3.5 bg-cyan-600 hover:bg-cyan-500 text-black font-mono text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
                    >
                      <Play className="w-4 h-4 fill-current" />
                      {isApiLoading ? "DISPATCHING SMTP HANDSHAKES..." : "COMPILE AND DELIVER JSON PAYLOAD"}
                    </button>
                  </form>

                  {/* Right Response block (6 cols) */}
                  <div className="xl:col-span-6 space-y-4 text-left font-mono">
                    <div className="flex justify-between items-center text-[10px] text-slate-500 font-bold uppercase">
                      <span>INTELLIGENT RESPONSE CODE TELEMETRY</span>
                      <span className="text-emerald-400 font-black">HTTP/1.1 200 OK</span>
                    </div>

                    <div className="bg-black border border-cyan-950 rounded-xl p-5 h-[34rem] font-mono text-xs text-slate-300 overflow-y-auto custom-scrollbar">
                      {apiResponse ? (
                        <pre className="text-left select-text whitespace-pre-wrap">{apiResponse}</pre>
                      ) : (
                        <div className="h-full flex flex-col justify-center items-center text-center text-slate-600 space-y-2 uppercase">
                          <Terminal className="w-10 h-10 text-slate-800" />
                          <p className="text-[10px] tracking-wide">
                            [ Standby... Sandbox compiler dormant ]
                          </p>
                          <p className="text-[9px] max-w-xs leading-normal">
                            Complete payload structures on the left panel block and initiate transmission validation to review response objects.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </motion.div>
            )}

            {/* VIEW 5: OUTBOX LIST */}
            {activeTab === "outbox" && (
              <motion.div
                key="outbox"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <FileText className="w-5 h-5 text-cyan-400" /> AUDITED OUTBOX QUEUES MATRIX
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Complete immutable transactional logs with 0.8s verification timestamps
                  </p>
                </div>

                <div className="space-y-3">
                  {outboxList.length === 0 ? (
                    <div className="p-20 text-center font-mono text-xs text-slate-500 uppercase">
                      NO HISTORIC EMAILS DISPATCHED
                    </div>
                  ) : (
                    outboxList.map((mail) => (
                      <div
                        key={mail.id}
                        className="p-5 rounded-2xl bg-[#030611] border border-cyan-950/70 hover:border-cyan-500/10 transition-all grid grid-cols-1 xl:grid-cols-12 gap-4 items-center"
                      >
                        <div className="xl:col-span-12 flex justify-between items-center text-[9px] font-mono text-slate-500 border-b border-slate-900 pb-2">
                          <span className="uppercase font-bold">MAIL ID REF: {mail.id}</span>
                          <span>UTC SENT: {new Date(mail.timestamp).toLocaleString("en-GB")}</span>
                        </div>

                        <div className="xl:col-span-8 text-left space-y-1">
                          <span className="block text-[10px] text-[#e840f3] font-mono uppercase font-black">
                            RECIPIENT TO: {mail.to}
                          </span>
                          <h4 className="text-xs font-sans font-black text-white uppercase leading-snug">
                            {mail.subject}
                          </h4>
                          <span className="block text-[9px] text-slate-500 font-mono">
                            VERIFIED ORIGIN CONDUIT: {mail.from}
                          </span>
                        </div>

                        <div className="xl:col-span-4 flex items-center xl:justify-end gap-3 font-mono text-[10px]">
                          <span className="text-slate-500 block">LATENCY:</span>
                          <span className="text-cyan-400 font-bold bg-cyan-950/20 px-2.5 py-1 rounded border border-cyan-500/20">
                            {mail.latencyMs}ms
                          </span>
                          
                          <span className="px-2.5 py-1 rounded border border-emerald-500/20 bg-emerald-950/25 text-emerald-400 font-bold uppercase">
                            DELIVERED LIVE
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>

              </motion.div>
            )}

          </AnimatePresence>

        </div>
      </div>

    </div>
  );
};
